// Interfaces
import {
    DocumentFileTypeEnum,
    IBasicDocumentInfo,
    IDocumentAddDialogData,
    IBasicDocument,
} from '../documents/IDocuments';
import { IApiResult, IApiListResult } from '../interfaces/IGeneral';
import { GUIDType } from '../interfaces/types';

// Api
import ApiBase from './ApiBase';
import { publicApiUrl } from './apiEndpoints';
import documentApi from './documentApi';

interface ProfilePictureCache {
    id: GUIDType;
    loading: Promise<IApiListResult<IBasicDocument> | null> | null;
    result: IApiResult<IBasicDocument> | null;
    status: 'waiting' | 'loading' | 'loaded';
    resolve: ResolveLoaded;
    reject: RejectLoaded;
}

type ResolveLoaded = (profilePicture: IApiResult<IBasicDocument> | null) => void;
type RejectLoaded = () => void;

class ProfilePictureApi extends ApiBase {
    private readonly cache: ProfilePictureCache[] = [];
    private waitingTimeout: NodeJS.Timeout | null = null;

    getProfilePicture(id: GUIDType) {
        return new Promise<IApiResult<IBasicDocument> | null>((resolve, reject) => {
            const cached = this.cache.find((i) => i.id === id);

            if (!cached) this.queue(id, resolve, reject);
            else {
                if (cached.status === 'loaded') this.resolveLoaded(id, resolve, reject);
                else if (cached.status === 'waiting' || cached.status === 'loading') {
                    cached.loading?.then(
                        () => this.resolveLoaded(id, resolve, reject),
                        () => this.resolveLoaded(id, resolve, reject)
                    );
                }
            }
        });
    }

    private resolveLoaded(id: GUIDType, resolve: ResolveLoaded, reject: RejectLoaded) {
        const cached = this.cache.find((i) => i.id === id);

        if (cached?.status === 'loaded' && cached.result) resolve(cached.result);
        else {
            resolve(null);
            reject();
        }
    }

    private queue(id: GUIDType, resolve: ResolveLoaded, reject: RejectLoaded) {
        this.cache.push({
            id,
            loading: null,
            result: null,
            status: 'waiting',
            resolve,
            reject,
        });

        if (this.waitingTimeout) clearTimeout(this.waitingTimeout);

        this.waitingTimeout = setTimeout(() => {
            const waiting = this.cache.filter((i) => i.status === 'waiting');
            const ids = waiting.map((i) => i.id);
            const promise = this.getProfilePictures(ids);

            waiting.forEach((w) => {
                w.loading = promise;
                promise.then((response) => {
                    const items = response?.Items || [];
                    const index = items.findIndex((i) => i.Owner === w.id);
                    const found = index !== -1;

                    w.status = 'loaded';
                    w.result = response && found ? this.mapListToSEntity(response, index) : null;
                    w.resolve(w.result);
                    if (!found) w.reject();
                });
            });
        }, 100);
    }

    private getProfilePictures(ids: string[]) {
        ids.forEach((id) => {
            const index = this.cache.findIndex((i) => i.id === id);
            if (index !== -1) this.cache[index].status = 'loading';
        });

        const promise = this.postList<IBasicDocument>(`${publicApiUrl}/profile-pictures`, ids, {
            incompatibleResult: true,
        });

        ids.forEach((id) => {
            const index = this.cache.findIndex((i) => i.id === id);
            if (index !== -1) this.cache[index].loading = promise;
        });

        return promise;
    }

    uploadProfilePicture(data: IDocumentAddDialogData) {
        return new Promise<IApiResult<IBasicDocument> | null>((resolve, reject) => {
            const fail = () => {
                resolve(null);
                reject();
            };

            documentApi.uploadDocument(data, DocumentFileTypeEnum.Image).then(
                () => {
                    const index = this.cache.findIndex((i) => i.id === data.owner);
                    if (index !== -1) this.cache.splice(index, 1);

                    this.getProfilePicture(data.owner).then(
                        (response) => (response ? resolve(response) : fail()),
                        () => fail()
                    );
                },
                () => fail()
            );
        });
    }
}

export default new ProfilePictureApi();
